import { T } from '@transifex/react';
import { Text, VStack } from '@vidahealth/vida-ui';

import { ScreenHeading } from '#modules/ui/ScreenHeading';

import { OTHER_CHOICE_VALUE } from '../../../../types/assessmentQuestion';
import type { QuestionFieldHandlers, QuestionFieldState } from '../../../../types/questionField';
import { getQuestionFlags, type QuestionFlags } from '../../../../utils/questionFlags';
import type { ActiveQuestion } from '../../../../utils/resolveQuestionChoices';

import { ChoiceField } from './ChoiceField';
import { DateField } from './DateField';
import { FreeTextField } from './FreeTextField';
import { RepeatingListField } from './RepeatingListField';

export type QuestionFieldProps = {
  question: ActiveQuestion;
  /**
   * `'screen'` — the question's own title/subtitle render as the page's
   * `ScreenHeading` (today's one-question-per-screen behavior).
   * `'field'` — the question's own title renders as a plain label above its
   * input, because the PAGE's title/description already own the
   * `ScreenHeading` (a `singleScreen` node's grouped render).
   */
  headingVariant: 'screen' | 'field';
  /** Per-assessment testID prefix (e.g. `phq-gad`); derives stable selectors for
   * this question's input, error live-region and each answer row. Omitted → no
   * testIDs. */
  testIDPrefix?: string;
  isSubmitting?: boolean;
  /** This question's pending values, errors and rows. */
  state: QuestionFieldState;
  /** This question's callbacks, all wired by `useAssessmentPendingState`. */
  handlers: QuestionFieldHandlers;
};

type QuestionHeadingProps = Pick<QuestionFieldProps, 'question' | 'headingVariant'>;

function QuestionHeading({ question, headingVariant }: QuestionHeadingProps) {
  if (headingVariant === 'screen') {
    return (
      <ScreenHeading title={question.text ?? ''} subtitle={question.additionalInfo ?? undefined} />
    );
  }
  if (!question.text && !question.additionalInfo) return null;
  // additionalInfo carries more than instructional copy ("Check all that
  // apply.") — some elements' description IS the consent/legal text (e.g. the
  // fax-number question's medical-records disclosure). It has nowhere else to
  // go once the page owns the single ScreenHeading, so it renders as a
  // secondary line under the field label rather than being dropped.
  return (
    <VStack space='xs'>
      {question.text ? (
        <Text size='md' weight='semibold' className='text-typography-900'>
          {question.text}
        </Text>
      ) : null}
      {question.additionalInfo ? (
        <Text size='sm' className='text-typography-600'>
          {question.additionalInfo}
        </Text>
      ) : null}
    </VStack>
  );
}

type QuestionInputProps = Omit<QuestionFieldProps, 'headingVariant'> & { flags: QuestionFlags };

/** Picks the one input this question's shape calls for. */
function QuestionInput({
  question,
  flags,
  state,
  handlers,
  testIDPrefix,
  isSubmitting = false,
}: QuestionInputProps) {
  const inputTestID = testIDPrefix ? `${testIDPrefix}-input` : undefined;

  if (flags.isDate) {
    return (
      <DateField
        value={state.dateValue}
        error={state.dateError}
        onChangeText={handlers.onDateChange}
        onBlur={handlers.onDateBlur}
        testID={inputTestID}
      />
    );
  }

  if (flags.isFreeText) {
    return (
      <FreeTextField
        value={state.textValue}
        isComment={flags.isComment}
        isNumeric={flags.isNumericFreeText}
        isTel={flags.isTelFreeText}
        numberError={state.numberError}
        phoneError={state.phoneError}
        onChangeText={handlers.onTextChange}
        onNumberBlur={handlers.onNumberBlur}
        onPhoneBlur={handlers.onPhoneBlur}
        testID={inputTestID}
      />
    );
  }

  if (flags.isRepeatingList) {
    return (
      <RepeatingListField
        question={question}
        rows={state.rows}
        statusMessage={state.statusMessage}
        isSubmitting={isSubmitting}
        onUpdateRowValue={handlers.onUpdateRowValue}
        onValidateRowCell={handlers.onValidateRowCell}
        onRemoveRow={handlers.onRemoveRow}
        onAddRowInline={handlers.onAddRowInline}
        testIDPrefix={testIDPrefix}
      />
    );
  }

  return (
    <ChoiceField
      question={question}
      selected={state.selected}
      isMultiSelect={flags.isMultiSelect}
      hasOtherItem={flags.hasOtherItem}
      otherInput={state.otherInput}
      onToggleChoice={handlers.onToggleChoice}
      onOtherInputChange={handlers.onOtherInputChange}
      testIDPrefix={testIDPrefix}
    />
  );
}

/**
 * Renders ONE question: its heading/label, its own required-answer banner, and
 * the one input its shape calls for — the unit a layout repeats once per
 * screen (`SingleQuestionScreen`) or once per question on a `singleScreen`
 * node's single scrollable screen (`GroupedQuestionsScreen`). Holds no state
 * of its own; every value and mutation arrives in `state`/`handlers` so the
 * same markup works unchanged for either caller.
 */
export function QuestionField({
  question,
  headingVariant,
  testIDPrefix,
  isSubmitting = false,
  state,
  handlers,
}: QuestionFieldProps) {
  const flags = getQuestionFlags(question);

  // Tighter than GroupedQuestionsScreen's own between-question gap: pairs a
  // question's heading/error with its own input instead of spacing them as
  // evenly as the gap to the next question, which read as one continuous
  // block (Siming, Sep 3 design review, "Your habits" section).
  return (
    <VStack space='md'>
      <QuestionHeading question={question} headingVariant={headingVariant} />

      {state.showRequiredError ? (
        <Text className='text-error-700'>
          {flags.isFreeText ||
          flags.isRepeatingList ||
          (flags.hasOtherItem && state.selected.includes(OTHER_CHOICE_VALUE)) ? (
            <T _str='Please enter an answer to continue.' />
          ) : (
            <T _str='Please select an answer to continue.' />
          )}
        </Text>
      ) : null}

      <QuestionInput
        question={question}
        flags={flags}
        state={state}
        handlers={handlers}
        testIDPrefix={testIDPrefix}
        isSubmitting={isSubmitting}
      />
    </VStack>
  );
}
